import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import { products } from '../data/products'

function ProductCard({
  product,
  isActive,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  product: (typeof products)[0]
  isActive: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  onClick: () => void
}) {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      className={`group relative cursor-pointer rounded-2xl p-6 transition-all duration-300 ${
        isActive
          ? 'bg-indigo-500 shadow-xl shadow-indigo-100'
          : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <h3
          className={`text-lg font-bold leading-snug ${
            isActive ? 'text-white' : 'text-gray-900'
          }`}
        >
          {product.name}
        </h3>
        <div
          className={`shrink-0 flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
            isActive ? 'bg-white text-indigo-600' : 'border border-gray-200 text-gray-400'
          }`}
        >
          <ArrowRightIcon
            className={`w-4 h-4 transition-transform duration-300 ${
              isActive ? '-rotate-45' : ''
            }`}
          />
        </div>
      </div>
      <p
        className={`mt-4 text-sm leading-relaxed ${
          isActive ? 'text-indigo-100' : 'text-gray-500'
        }`}
      >
        {product.shortDesc}
      </p>
    </div>
  )
}

export default function Products() {
  const navigate = useNavigate()
  const [hovered, setHovered] = useState<string | null>(null)
  const activeId = hovered ?? products[0].id

  const handleClick = (id: string) => navigate(`/products/${id}`)

  return (
    <section id="products" className="scroll-mt-20 bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-5xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Products That Power{' '}
            <span className="text-indigo-500">Payments</span>
          </h2>
          <p className="mt-4 text-base text-gray-500">
            Simplify your payment ecosystem with our ready-to-use L3 Transit App.
          </p>
        </div>

        {/* Desktop: alternating timeline */}
        <div className="relative mt-20 hidden lg:block">
          {/* Vertical center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-indigo-100" />

          <div className="space-y-4">
            {products.map((product, index) => {
              const isRight = index % 2 === 0
              const isActive = activeId === product.id

              return (
                <div key={product.id} className="relative grid grid-cols-2 items-center gap-12">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-3.5 h-3.5 rounded-full border-2 transition-colors duration-300 ${
                      isActive
                        ? 'bg-indigo-500 border-indigo-500'
                        : 'bg-white border-indigo-200'
                    }`}
                  />

                  {/* Left slot */}
                  <div className="pr-8">
                    {!isRight && (
                      <ProductCard
                        product={product}
                        isActive={isActive}
                        onMouseEnter={() => setHovered(product.id)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleClick(product.id)}
                      />
                    )}
                  </div>

                  {/* Right slot */}
                  <div className="pl-8">
                    {isRight && (
                      <ProductCard
                        product={product}
                        isActive={isActive}
                        onMouseEnter={() => setHovered(product.id)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleClick(product.id)}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Mobile: single column */}
        <div className="mt-12 space-y-4 lg:hidden">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isActive={activeId === product.id}
              onMouseEnter={() => setHovered(product.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => handleClick(product.id)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
